import { useEffect, useState, useCallback } from 'react';

import {
  CheckCircleFilled,
  ClockCircleFilled,
  DollarCircleOutlined,
  PercentageOutlined,
  PlayCircleFilled,
  StopFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import * as S from './styled';

import { getVouchers } from '../../../hooks/vouchers';
import { COLORS } from '../../../utils/theme/colors';
import ChargerIcon from '../../atoms/icons/ChargerIcon';

interface DataType {
  kind: string;
  code: string;
  title: string;
  units: number;
  used_total: number;
  start: string;
  expire: string;
  status: string;
}

export const useVoucherTable = () => {
  const [vouchers, setVouchers] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: null,
    pagination: { page: 1, pageSize: 10 },
    filters: { code: '', title: '', description: '', kind: '', status: '' },
  });

  const [filters, setFilters] = useState<any>({ code: '' });
  const [voucherSuccess, setVoucherSuccess] = useState(false);

  const onChangeField = (field: string, value: any) => {
    setFilters({ ...filters, [field]: value });
  };

  const tryToGetVouchers = useCallback(
    async (
      page: number = 1,
      pageSize: number = 10,
      code: string = '',
      title: string = '',
      description: string = '',
      kind: string = '',
      status: string = ''
    ) => {
      setVouchers((prevVouchers) => ({ ...prevVouchers, loading: true, error: false }));
      setFilters({ code, title, description, kind, status });

      const vouchersResponse: any = await getVouchers(page, pageSize, code, title, description, kind, status);

      if (!vouchersResponse.error) {
        return setVouchers((prevVouchers) => ({
          ...prevVouchers,
          error: false,
          loading: false,
          data: vouchersResponse,
          pagination: { page, pageSize },
        }));
      }

      if (vouchersResponse.status === 400) {
        return setVouchers((prevVouchers) => ({
          ...prevVouchers,
          error: true,
          loading: false,
          data: null,
        }));
      }

      return setVouchers((prevVouchers) => ({ ...prevVouchers, loading: false }));
    },
    [setVouchers, setFilters]
  );

  useEffect(() => {
    setVouchers((prevVouchers) => ({ ...prevVouchers, request: tryToGetVouchers }));
  }, [tryToGetVouchers]);

  const columns: ColumnsType<DataType> = [
    {
      title: 'Código do voucher',
      dataIndex: 'code',
      key: 'code',
      render: (value, row: any) => <S.Link to={`/voucher/${row.voucher_pk}`}>{value.toUpperCase()}</S.Link>,
    },
    {
      title: 'Nome do voucher',
      dataIndex: 'title',
      key: 'title',
      defaultSortOrder: 'descend',
      sortDirections: ['descend'],
    },
    {
      title: 'Qtd. total',
      dataIndex: 'units',
      key: 'units',
      align: 'center',
    },
    {
      title: 'Qtd. consumida',
      dataIndex: 'used_total',
      key: 'used_total',
      align: 'center',
    },
    {
      title: 'Data de início',
      dataIndex: 'start',
      key: 'start',
      align: 'center',
    },
    {
      title: 'Validade até',
      dataIndex: 'expire',
      key: 'expire',
      align: 'center',
    },
    {
      title: 'Tipo',
      dataIndex: 'kind',
      key: 'kind',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
  ];

  const getStatus = (statusEnum: string) => {
    const statusDescription = {
      NOT_STARTED: {
        icon: <PlayCircleFilled style={{ color: COLORS.carBrandUsage }} />,
        text: 'Não iniciado',
      },
      ACTIVE: {
        icon: <CheckCircleFilled style={{ color: COLORS.available }} />,
        text: 'Ativo',
      },
      INACTIVE: {
        icon: <StopFilled style={{ color: COLORS.busy }} />,
        text: 'Inativo',
      },
      EXPIRED: {
        icon: <StopFilled style={{ color: COLORS.busy }} />,
        text: 'Expirado',
      },
      USED: {
        icon: <ClockCircleFilled style={{ color: COLORS.consume }} />,
        text: 'Usado',
      },
    };

    return (
      <>
        {statusDescription[statusEnum].icon} {statusDescription[statusEnum].text}
      </>
    );
  };

  const getKind = (kind) => {
    const kinds = {
      PERCENT_RECHARGE: {
        icon: <PercentageOutlined style={{ marginRight: 8 }} />,
        text: 'Porcentagem',
      },
      MONEY_RECHARGE: {
        icon: <DollarCircleOutlined style={{ marginRight: 8 }} />,
        text: 'Reais',
      },
      KWH_RECHARGE: {
        icon: <ChargerIcon fill='rgba(0, 0, 0, 0.7)' style={{ marginRight: 8, position: 'relative', top: 2 }} />,
        text: 'Quilowatt-hora',
      },
    };

    return (
      <>
        {kinds[kind].icon}
        {kinds[kind].text}
      </>
    );
  };

  const dataSource: DataType[] = vouchers?.data?.results?.map((voucher: any, index: number) => {
    return {
      key: `vouchers-${voucher.code}-${index}`,
      voucher_pk: voucher.voucher_pk,
      code: voucher.code,
      title: voucher.title,
      units: voucher.units,
      used_total: voucher.used_total,
      start: moment(voucher.start).format('DD/MM/YYYY HH:mm'),
      expire: moment(voucher.expire).format('DD/MM/YYYY HH:mm'),
      kind: getKind(voucher.kind),
      status: getStatus(voucher.status),
    };
  });

  useEffect(() => {
    (async () => {
      await tryToGetVouchers();
    })();
  }, [tryToGetVouchers]);

  return {
    vouchers,
    voucherSuccess,
    setVoucherSuccess,
    columns,
    dataSource,
    onChangeField,
    filters,
  };
};
